import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  GetChatsFilterDto,
  groupKitChatControllerCreateChat,
  groupKitChatControllerDeleteChat,
  groupKitChatControllerGetChat,
  groupKitChatControllerGetChats,
  groupKitChatControllerUpdateChat,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

export const useChatsData = (
  groupUuid?: string | null,
  kitUuid?: string | null,
  filter?: GetChatsFilterDto,
) => {
  const queryClient = useQueryClient();

  const {
    data: chats,
    isPending,
    error,
    refetch,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    queryKey: queryKeys.kitChats(groupUuid!, kitUuid!, filter),
    queryFn: () =>
      groupKitChatControllerGetChats({
        path: {
          kitUuid: kitUuid!,
          groupUuid: groupUuid!,
        },
        query: {
          filter,
        },
      }).then((res) => res.data?.data ?? []),
  });

  const { mutateAsync: createChat } = useMutation({
    mutationFn: (vars: {
      name?: string;
      overwriteKitUuid?: string;
      expertUuids: string[];
      transcriptChatUuid?: string;
    }) =>
      groupKitChatControllerCreateChat({
        path: {
          kitUuid: vars.overwriteKitUuid ?? kitUuid!,
          groupUuid: groupUuid!,
        },
        body: {
          name: vars.name,
          expertUuids: vars.expertUuids,
          transcriptChatUuid: vars.transcriptChatUuid,
        },
        throwOnError: true,
      }).then((res) => res.data.uuid),
    onSettled: () => {
      refetch();
    },
  });

  const { mutateAsync: updateChat } = useMutation({
    mutationFn: (vars: { uuid: string; name: string }) =>
      groupKitChatControllerUpdateChat({
        path: {
          chatUuid: vars.uuid,
          kitUuid: kitUuid!,
          groupUuid: groupUuid!,
        },
        body: {
          name: vars.name,
        },
      }),
    onSettled: (_data, _error, vars) => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitChat(groupUuid!, kitUuid!, vars.uuid),
      });
    },
  });

  const { mutateAsync: deleteChat } = useMutation({
    mutationFn: (chatUuid: string) =>
      groupKitChatControllerDeleteChat({
        path: {
          kitUuid: kitUuid!,
          groupUuid: groupUuid!,
          chatUuid: chatUuid,
        },
      }).then((res) => res.data?.uuid),
    onSettled: (_data, _error, chatUuid) => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitChat(groupUuid!, kitUuid!, chatUuid),
      });
    },
  });

  return {
    chats: chats ?? [],
    isLoading: isPending,
    error,
    createChat,
    updateChat,
    deleteChat,
  };
};

export const useChatData = (
  groupUuid?: string | null,
  kitUuid?: string | null,
  chatUuid?: string | null,
) => {
  const { data: chat, isPending } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid && !!chatUuid,
    queryKey: queryKeys.kitChat(groupUuid!, kitUuid!, chatUuid!),
    queryFn: () =>
      groupKitChatControllerGetChat({
        path: {
          kitUuid: kitUuid!,
          groupUuid: groupUuid!,
          chatUuid: chatUuid!,
        },
      }).then((res) => res.data ?? null),
  });

  return {
    chat: chat ?? null,
    isLoading: chatUuid === null ? false : isPending,
  };
};
